import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { sanitizeNameInput } from '../utils/validation';
import './Profile.css';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({ prenom: '', nom: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        prenom: user.prenom || '',
        nom: user.nom || '',
      });
    }
    setLoading(false);
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = (name === 'prenom' || name === 'nom') ? sanitizeNameInput(value) : value;
    setFormData({ ...formData, [name]: sanitized });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await api.patch('/auth/profile/update/', formData);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const errData = err.response?.data;
      const message = errData?.nom?.[0] || errData?.prenom?.[0] || errData?.detail || t('profile.saveError') || 'Erreur lors de la sauvegarde';
      setError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Navbar />
        <div className="profile-content">
          <div className="profile-loading">{t('profile.loading') || 'Chargement...'}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-content">
        <div className="profile-card">
          <h1>{t('profile.title') || 'Mon profil'}</h1>

          <div className="profile-info">
            <h2 className="profile-section-title">{t('profile.currentInfo') || 'Vos informations'}</h2>
            <div className="profile-field profile-email">
              <label>{t('auth.email')}</label>
              <span>{user.email}</span>
              <p className="profile-field-hint">{t('profile.emailReadOnly') || 'L\'email ne peut pas être modifié'}</p>
            </div>
            <div className="profile-field">
              <label>{t('auth.firstName')}</label>
              <span>{user.prenom || (t('profile.notFilled') || 'Non renseigné')}</span>
            </div>
            <div className="profile-field">
              <label>{t('auth.lastName')}</label>
              <span>{user.nom || (t('profile.notFilled') || 'Non renseigné')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="profile-form">
            <h2 className="profile-section-title">{t('profile.editInfo') || 'Modifier vos informations'}</h2>
            {error && <div className="profile-error">{error}</div>}

            <div className="profile-form-group">
              <label htmlFor="prenom">{t('auth.firstName')}</label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                placeholder={t('auth.firstName')}
                maxLength={150}
              />
            </div>

            <div className="profile-form-group">
              <label htmlFor="nom">{t('auth.lastName')}</label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                placeholder={t('auth.lastName')}
                maxLength={150}
              />
            </div>

            <div className="profile-form-actions">
              <button type="submit" className="profile-save-button" disabled={saving}>
                {saving ? (t('profile.saving') || 'Enregistrement...') : (t('profile.save') || 'Enregistrer')}
              </button>
              {saved && <span className="profile-saved">{t('profile.saved') || '✓ Enregistré !'}</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
